import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/services/api';
import { getDefaultPathForRole } from '@/config/routes';
import { takeLoginRedirect } from '@/utils/loginRedirect';

export const useLogin = (customerId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = useCallback(async (email: string, password: string) => {
    if (!email || !password) {
      setError('이메일과 비밀번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.login(email, password, customerId || undefined);
      const { access_token, user: userData, redirect_url } = response.data;

      if (!access_token || !userData) {
        throw new Error('로그인 응답에 토큰 또는 사용자 정보가 없습니다.');
      }

      login(access_token, userData);
      setLoading(false);

      // 서비스 로그인(/oidc/authorize)에서 왔으면 그리로 먼저 돌아간다 (#226)
      const pending = takeLoginRedirect();
      if (pending) {
        navigate(pending);
        return;
      }

      // 백엔드에서 제공한 redirect_url 사용, 없으면 대시보드로
      if (redirect_url) {
        console.log('🔍 [useLogin] redirect_url from backend:', redirect_url);
        try {
          const url = new URL(redirect_url);
          console.log('🔍 [useLogin] parsed URL - hostname:', url.hostname, 'port:', url.port, 'pathname:', url.pathname, 'search:', url.search);
          console.log('🔍 [useLogin] window.location - hostname:', window.location.hostname, 'port:', window.location.port);

          // 현재 도메인과 같으면 navigate 사용 (쿼리 파라미터 포함)
          if (url.hostname === window.location.hostname && url.port === window.location.port) {
            console.log('✅ [useLogin] Same origin, using navigate:', url.pathname + url.search);
            navigate(url.pathname + url.search);
          } else {
            // 다른 도메인이면 window.location.href 사용
            console.log('⚠️ [useLogin] Different origin, using window.location.href:', redirect_url);
            window.location.href = redirect_url;
          }
        } catch (e) {
          console.error("Invalid redirect URL from backend, navigating to default page:", redirect_url, e);
          navigate(getDefaultPathForRole(userData.role));
        }
      } else {
        console.log('ℹ️ [useLogin] No redirect_url, navigating to default page');
        navigate(getDefaultPathForRole(userData.role));
      }
    } catch (error: any) {
      console.error('Login error:', error);
      let errorMessage = '로그인에 실패했습니다.';
      
      if (error.response?.status === 401) {
        errorMessage = error.response?.data?.detail || '이메일 또는 비밀번호가 올바르지 않습니다.';
      } else if (error.response?.status === 403) {
        errorMessage = error.response?.data?.detail || '로그인 권한이 없습니다. 관리자에게 문의하세요.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  }, [customerId, login, navigate]);

  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  return {
    loading,
    error,
    success,
    handleLogin,
    clearMessages,
    setError,
    setSuccess,
  };
};
